import dayjs from "dayjs";

import { AiSparkles, Gemini, AiMode, ChatGpt, Perplexity } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import type {
    IIntent,
    ITabData,
    ISentiment,
    IAiModelConfig,
    ExplicitLLModel,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import {
    VolatilityStatus,
    CitationGapStatus,
    EAvailableTabs,
    LLModel,
    SentimentStatus,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

const { green, red, yellow } = colorsPalettes;

export const ALL_TOPICS_NAME = "All Topics";
export const DEFAULT_TOPIC = ALL_TOPICS_NAME;
export const ALL_MODELS_NAME = "All";
export const MAX_DEFAULT_CAMPAIGN_DURATION = 7;
export const AI_TRACKER_DEFAULT_DURATION = "6d";
export const MAX_TRACKED_DOMAINS = 25;
export const MAX_TOPIC_NAME_LENGTH = 255;
export const DEFAULT_LLM = LLModel.GPT;
export const BLANK_LEGEND_COLOR = colorsPalettes.carbon[200];
export const ANNOTATIONS_TAG_PREFIX = "llm_tracker";

const GRAPH_ANNOTATION_CAMPAIGN_REENABLE_ACTION_SUFFIX = "campaign_reenabled";
const GRAPH_ANNOTATION_LLM_ACTION_SUFFIXES = ["llm_tracking_started", "llm_tracking_stopped"];
const GRAPH_ANNOTATION_PROMPT_ACTION_SUFFIXES = ["prompts_added", "prompts_removed"];
const GRAPH_ANNOTATION_TOPIC_ACTION_SUFFIXES = ["topic_added", "topic_removed"];

export const getGraphActionAnnotationFetchTagPrefixes = (includeTopicActions: boolean): string[] => {
    const suffixes = [
        GRAPH_ANNOTATION_CAMPAIGN_REENABLE_ACTION_SUFFIX,
        ...GRAPH_ANNOTATION_PROMPT_ACTION_SUFFIXES,
        ...GRAPH_ANNOTATION_LLM_ACTION_SUFFIXES,
        ...(includeTopicActions ? GRAPH_ANNOTATION_TOPIC_ACTION_SUFFIXES : []),
    ];

    return suffixes.map((suffix) => `${ANNOTATIONS_TAG_PREFIX}_action:${suffix}`);
};

export const getAvailableTabsConfig = (): Partial<Record<EAvailableTabs, ITabData>> => {
    const rawDate = (swSettings.components as Record<string, Record<string, Record<string, unknown>>>)?.AiTracker
        ?.resources?.FirstAvailableSentimentDate as string | undefined;

    return {
        [EAvailableTabs.BRANDS]: {
            title: "llm.reports.tab.brand.title",
            tooltip: "llm.reports.tab.brand.tooltip",
            stateId: "digitalsuite_llm_solutions_brand_report",
        },
        [EAvailableTabs.PROMPTS]: {
            title: "llm.reports.tab.prompts.title",
            tooltip: "llm.reports.tab.prompts.tooltip",
            stateId: "digitalsuite_llm_solutions_prompts_report",
        },
        [EAvailableTabs.SOURCES]: {
            title: "llm.reports.tab.sources.title",
            tooltip: "llm.reports.tab.sources.tooltip",
            stateId: "digitalsuite_llm_solutions_sources_report",
        },
        [EAvailableTabs.SENTIMENT]: {
            title: "llm.reports.tab.sentiment.title",
            stateId: "digitalsuite_llm_solutions_sentiment_report",
            firstSupportedDate: rawDate ? dayjs.utc(rawDate) : undefined,
        },
    };
};

export const getSentimentStatusConfig = (): Record<SentimentStatus, IIntent> => ({
    [SentimentStatus.Positive]: {
        label: "POSITIVE",
        text: translate("llm.tracker.sentiment.positive"),
        color: colorsPalettes.green[100],
        textColor: colorsPalettes.green[500],
    },
    [SentimentStatus.Negative]: {
        label: "NEGATIVE",
        text: translate("llm.tracker.sentiment.negative"),
        color: colorsPalettes.red[100],
        textColor: colorsPalettes.red[500],
    },
    [SentimentStatus.Neutral]: {
        label: "NEUTRAL",
        text: translate("llm.tracker.sentiment.neutral"),
        color: colorsPalettes.yellow[100],
        textColor: colorsPalettes.yellow[600],
    },
    [SentimentStatus.NotMentioned]: {
        label: "NOT MENTIONED",
        text: translate("llm.tracker.sentiment.not_mentioned"),
        color: colorsPalettes.carbon[50],
        textColor: colorsPalettes.carbon[400],
    },
});

export const getVolatilityStatusConfig = (): Record<VolatilityStatus, IIntent> => ({
    [VolatilityStatus.Stable]: {
        label: "STABLE",
        text: translate("llm.tracker.volatility.stable"),
        color: colorsPalettes.green[100],
        textColor: colorsPalettes.carbon[400],
    },
    [VolatilityStatus.Moderate]: {
        label: "MODERATE",
        text: translate("llm.tracker.volatility.moderate"),
        color: colorsPalettes.yellow[100],
        textColor: colorsPalettes.carbon[400],
    },
    [VolatilityStatus.Volatile]: {
        label: "VOLATILE",
        text: translate("llm.tracker.volatility.volatile"),
        color: colorsPalettes.red[100],
        textColor: colorsPalettes.carbon[400],
    },
});

export const getCitationGapStatusConfig = (): Record<CitationGapStatus, IIntent> => ({
    [CitationGapStatus.None]: {
        label: "NONE",
        text: translate("llm.tracker.citation_gap.none"),
        color: colorsPalettes.carbon[25],
        textColor: colorsPalettes.carbon[400],
    },
    [CitationGapStatus.Partial]: {
        label: "PARTIAL",
        text: translate("llm.tracker.citation_gap.partial"),
        color: colorsPalettes.yellow[100],
        textColor: colorsPalettes.carbon[400],
    },
    [CitationGapStatus.Full]: {
        label: "FULL",
        text: translate("llm.tracker.citation_gap.full"),
        color: colorsPalettes.red[100],
        textColor: colorsPalettes.carbon[400],
    },
});

export const DEFAULT_SENTIMENT: ISentiment = {
    positive: 0,
    neutral: 0,
    negative: 0,
};

export const SENTIMENT_COLOR_SET: Record<string, string> = {
    positive: green[300],
    neutral: yellow[200],
    negative: red[300],
};

export const AI_MODELS_CONFIG: Record<ExplicitLLModel, IAiModelConfig> = {
    [LLModel.GPT]: {
        displayNameKey: "llm.model_selector.model.gpt",
        Icon: ChatGpt,
        iconName: "chat-gpt",
    },
    [LLModel.AiMode]: {
        displayNameKey: "llm.model_selector.model.aimode",
        Icon: AiMode,
        iconName: "ai-mode",
    },
    [LLModel.Gemini]: {
        displayNameKey: "llm.model_selector.model.gemini",
        Icon: Gemini,
        iconName: "gemini",
    },
    [LLModel.Perplexity]: {
        displayNameKey: "llm.model_selector.model.perplexity",
        Icon: Perplexity,
        iconName: "perplexity",
    },
    [LLModel.Claude]: {
        displayNameKey: "llm.model_selector.model.claude",
        Icon: AiSparkles,
        iconName: "claude",
        isNotSupported: true,
    },
};

export const AI_MODELS_SORT_ORDER = Object.keys(AI_MODELS_CONFIG) as LLModel[];
